import { isSubagentSessionKey } from "../../../src/routing/session-key.js";
import { parseAgentSessionKey } from "../../../src/sessions/session-key-utils.js";
import { clearRunArtifactPreviews } from "./app-run-artifacts.js";
import { scheduleChatScroll } from "./app-scroll.js";
import { setLastActiveSessionKey, syncUrlWithSessionKey } from "./app-settings.js";
import { resetToolStream } from "./app-tool-stream.js";
import type { OpenClawApp } from "./app.js";
import {
  pendingEditMatchesSession,
  type ChatEditRewindConfirmPayload,
  type ChatPendingMessageEdit,
} from "./chat/chat-pending-edit.js";
import {
  clearPlanImplementDismissForSession,
  dismissPlanImplementFingerprint,
  evaluatePlanImplementOffer,
  PLAN_IMPLEMENT_PROMPT,
} from "./chat/chat-plan-implement-offer.js";
import {
  clearChatRunArtifactsBaseline,
  clearFileChangesNoticeForSession,
  getChatRunArtifactsSinceTs,
  markChatRunArtifactsBaseline,
} from "./chat/chat-run-artifacts.js";
import { normalizeChatOpenTabs } from "./chat/chat-tabs.ts";
import { clearChatWaitState } from "./chat/chat-wait-ticker.ts";
import {
  classifyAutoDelegation,
  parseMessageForSubAgent,
  shouldSendToMainSessionInsteadOfSpawn,
  pickOllamaModel,
  shouldRouteTrivialToOllama,
  getActiveAgentIds,
  pickBestFitAgentIds,
  type GatewayModelChoice,
} from "./chat/delegate-router.js";
import {
  applyFleetSendOptionsToMessage,
  deriveProjectNameFromMessage,
} from "./chat/fleet-chat-send-options.js";
import { clearFleetCompletionNoticeForSession } from "./chat/fleet-completion-notice.js";
import type { FleetComposerMode } from "./chat/fleet-composer-mode.js";
import {
  resolveComposerModeSendOptions,
  resolveEffectiveChatComposerMode,
} from "./chat/fleet-composer-mode.js";
import {
  findChatContextProject,
  resolveSelfImprovementProjectId,
} from "./chat/fleet-projects-session.js";
import { formatFleetDelegationAck } from "./chat/fleet-send-routing-preview.js";
import { type MessageRewindTarget, rewindTargetToRewindParams } from "./chat/message-transcript.js";
import { subagentSpawnLabel } from "./chat/subagent-run-display.js";
import { loadAgents } from "./controllers/agents.js";
import {
  abortChatRun,
  injectChatMessage,
  loadChatHistory,
  regenerateChatReply,
  resetChatSession,
  rewindChatBeforeMessage,
  sendChatMessage,
  steerChatMessage,
  appendOptimisticUserMessage,
  type ResetChatSessionResult,
} from "./controllers/chat.js";
import { applyFleetSelfImprovementStarted } from "./controllers/fleet-self-improvement-ui.js";
import {
  formatFleetSelfImprovementError,
  startFleetSelfImprovement,
} from "./controllers/fleet-self-improvement.js";
import { loadSessions } from "./controllers/sessions.js";
import { applySpawnAndNotify } from "./controllers/subagent-spawn-ui.js";
import {
  createPendingSubagentCard,
  loadSubagentRegistryRuns,
  mergeRestoredSubagents,
  requestSessionsSpawn,
} from "./controllers/subagents.js";
import type { GatewayHelloOk } from "./gateway.js";
import { normalizeBasePath } from "./navigation.js";
import type { SessionsListResult } from "./types.js";
import type { ChatAttachment, ChatQueueItem, ChatSubagentCard } from "./ui-types.js";
import { generateUUID } from "./uuid.js";
import { flushComposeDraftFromDom, syncChatComposeDraft } from "./views/chat.js";

export type ChatHost = {
  connected: boolean;
  chatMessage: string;
  chatAttachments: ChatAttachment[];
  chatPendingMessageEdit?: ChatPendingMessageEdit | null;
  chatEditRewindConfirm?: ChatEditRewindConfirmPayload | null;
  chatQueue: ChatQueueItem[];
  chatRunId: string | null;
  chatSending: boolean;
  chatStreamStartedAt?: number | null;
  chatStream?: string | null;
  chatReasoningStream?: string | null;
  sessionKey: string;
  basePath: string;
  hello: GatewayHelloOk | null;
  chatAvatarUrl: string | null;
  refreshSessionsAfterChat: Set<string>;
  chatMessages?: unknown[];
  agentsList?: import("./types.js").AgentsListResult | null;
  chatSubagents?: ChatSubagentCard[];
  client?: import("./gateway.js").GatewayBrowserClient | null;
  sessionsResult?: SessionsListResult | null;
  onSubagentSpawned?: (card: ChatSubagentCard) => void;
  chatComposerMode?: FleetComposerMode;
  planImplementDismissedFingerprint?: Record<string, string>;
  /**
   * JAISIU-3503 — host-side hook fired after a successful abort. Used
   * by the app shell to surface a transient "Run stopped" toast so
   * every stop path (Stop button, /stop slash, footer Stop run) gives
   * the operator consistent visible feedback.
   */
  notifyRunStopped?: (sessionKey: string) => void;
  /**
   * JAISIU-4089 — host-side hook fired when a chat RPC (typically
   * `chat.send`) fails after exhausting its retry budget. The chat
   * controller invokes this synchronously on the failed path. The
   * app shell wires it to the session-health monitor so a burst of
   * failures flips the topbar pill to "degraded".
   */
  noteSessionHealthFailure?: (reason: string) => void;
};

function mergeSpawnResult(
  host: ChatHost,
  cards: ChatSubagentCard[],
  cardId: string,
  result: Parameters<typeof applySpawnAndNotify>[2],
): ChatSubagentCard[] {
  return applySpawnAndNotify(cards, cardId, result, (card) => host.onSubagentSpawned?.(card));
}

export const CHAT_SESSIONS_ACTIVE_MINUTES = 120;

export type ResetSessionIdentityHost = ChatHost & {
  applySettings: (next: import("./storage.ts").UiSettings) => void;
  settings: import("./storage.ts").UiSettings;
};

/**
 * After `sessions.reset`, keep persisted UI identity (settings, tabs, URL)
 * aligned with the canonical session key the gateway returned. Without this,
 * `chat.send` / WS `chat` events target the runtime key while refresh loads
 * `settings.sessionKey` — operator messages vanish and activity chrome stalls.
 */
export function applyResetSessionIdentityHost(
  host: ResetSessionIdentityHost,
  result: Extract<ResetChatSessionResult, { ok: true }>,
): void {
  const previousKey = result.previousKey.trim();
  const nextKey = result.nextKey.trim();
  if (!nextKey) {
    return;
  }
  host.sessionKey = nextKey;
  const rewriteTabs = (tabs: string[]) =>
    tabs.map((tab) => (tab.trim() === previousKey ? nextKey : tab));
  const tabs = normalizeChatOpenTabs(
    previousKey !== nextKey
      ? rewriteTabs(host.settings.chatOpenTabs ?? [])
      : host.settings.chatOpenTabs,
    nextKey,
  );
  host.applySettings({
    ...host.settings,
    sessionKey: nextKey,
    lastActiveSessionKey: nextKey,
    chatOpenTabs: tabs,
  });
  if (previousKey !== nextKey) {
    syncUrlWithSessionKey(
      host as unknown as Parameters<typeof syncUrlWithSessionKey>[0],
      nextKey,
      true,
    );
  }
}

export function isChatBusy(host: ChatHost) {
  return host.chatSending || Boolean(host.chatRunId);
}

export function isChatStopCommand(text: string) {
  const trimmed = text.trim();
  if (!trimmed) {
    return false;
  }
  const normalized = trimmed.toLowerCase();
  if (normalized === "/stop") {
    return true;
  }
  return (
    normalized === "stop" ||
    normalized === "esc" ||
    normalized === "abort" ||
    normalized === "wait" ||
    normalized === "exit"
  );
}

function isChatResetCommand(text: string) {
  const trimmed = text.trim();
  if (!trimmed) {
    return false;
  }
  const normalized = trimmed.toLowerCase();
  if (normalized === "/new" || normalized === "/reset") {
    return true;
  }
  return normalized.startsWith("/new ") || normalized.startsWith("/reset ");
}

export async function handleAbortChat(host: ChatHost) {
  if (!host.connected) {
    return;
  }
  const ok = await abortChatRun(host as unknown as OpenClawApp);
  if (ok) {
    const app = host as unknown as OpenClawApp;
    // Use the helper so the live-wait ticker interval is torn down
    // synchronously — a stranded interval would keep painting the
    // activity bubble with stale elapsed time until Lit's next render.
    clearChatWaitState(app as unknown as Parameters<typeof clearChatWaitState>[0]);
    void clearRunArtifactPreviews(app, host.sessionKey);
    resetToolStream(app as unknown as Parameters<typeof resetToolStream>[0]);
    // JAISIU-3503 — let the host surface a "Run stopped" toast so
    // every abort path (Stop button, /stop slash, footer Stop run)
    // gets consistent operator-visible feedback. The host owns the
    // timer + per-session routing; this is just the trigger.
    host.notifyRunStopped?.(host.sessionKey);
  }
}

/**
 * JAISIU-3297 UX-R8 — Stop thinking: abort + revert the code changes
 * made during the current prompt only. Confirm before reverting when a
 * snapshot baseline exists for this session so the user doesn't lose
 * work to an accidental click.
 */
export async function handleStopThinking(host: ChatHost) {
  if (!host.connected) {
    return;
  }
  const app = host as unknown as OpenClawApp;
  const sinceTs = getChatRunArtifactsSinceTs(app.chatRunArtifactsSinceTs, host.sessionKey);
  if (typeof sinceTs === "number") {
    const confirmed = window.confirm(
      "Stop thinking and revert the file changes made during this prompt? Earlier history is kept.",
    );
    if (!confirmed) {
      return;
    }
  }
  const ok = await abortChatRun(app, {
    sinceTs,
    projectId: app.chatActiveProjectId ?? null,
  });
  if (!ok) {
    return;
  }
  // The snapshot served its purpose — drop it so subsequent artifacts
  // surface against the post-revert state. Without this the dashboard
  // would keep claiming there is an open review window for files that
  // no longer exist on disk.
  app.chatRunArtifactsSinceTs = clearChatRunArtifactsBaseline(
    app.chatRunArtifactsSinceTs,
    host.sessionKey,
  );
  clearChatWaitState(app as unknown as Parameters<typeof clearChatWaitState>[0]);
  void clearRunArtifactPreviews(app, host.sessionKey);
  resetToolStream(app as unknown as Parameters<typeof resetToolStream>[0]);
  void loadChatHistory(app);
}

function enqueueChatMessage(
  host: ChatHost,
  text: string,
  attachments?: ChatAttachment[],
  refreshSessions?: boolean,
) {
  const trimmed = text.trim();
  const hasAttachments = Boolean(attachments && attachments.length > 0);
  if (!trimmed && !hasAttachments) {
    return;
  }
  host.chatQueue = [
    ...host.chatQueue,
    {
      id: generateUUID(),
      text: trimmed,
      createdAt: Date.now(),
      attachments: hasAttachments ? attachments?.map((att) => ({ ...att })) : undefined,
      refreshSessions,
    },
  ];
}

export async function sendChatMessageNow(
  host: ChatHost,
  message: string,
  opts?: {
    previousDraft?: string;
    restoreDraft?: boolean;
    attachments?: ChatAttachment[];
    previousAttachments?: ChatAttachment[];
    restoreAttachments?: boolean;
    refreshSessions?: boolean;
  },
) {
  resetToolStream(host as unknown as Parameters<typeof resetToolStream>[0]);
  const app = host as unknown as OpenClawApp;
  app.wireChatControllerState(app);
  app.chatLiveRunHint = null;
  app.chatActiveFileToolPath = null;
  void clearRunArtifactPreviews(app, host.sessionKey);
  const runId = await sendChatMessage(app, message, opts?.attachments);
  if (runId) {
    const agentId = host.sessionKey.split(":")[1] ?? "main";
    app.chatMainRun = {
      id: "main-run",
      runId,
      childSessionKey: host.sessionKey,
      label: "Main agent",
      agentId,
      task: message.slice(0, 240),
      status: "running",
      createdAt: Date.now(),
      updatedAt: Date.now(),
      events: [],
      phase: "start",
    };
  }
  const ok = Boolean(runId);
  if (!ok && opts?.previousDraft != null) {
    host.chatMessage = opts.previousDraft;
  }
  if (!ok && opts?.previousAttachments) {
    host.chatAttachments = opts.previousAttachments;
  }
  if (ok) {
    const runStart = Date.now();
    app.chatRunArtifactsSinceTs = markChatRunArtifactsBaseline(
      app.chatRunArtifactsSinceTs,
      host.sessionKey,
      runStart,
    );
    app.fileChangesNotifiedFingerprint = clearFileChangesNoticeForSession(
      app.fileChangesNotifiedFingerprint,
      host.sessionKey,
    );
    app.fleetFileChangesNotifiedFingerprint = clearFileChangesNoticeForSession(
      app.fleetFileChangesNotifiedFingerprint,
      host.sessionKey,
    );
    app.fleetCompletionNotifiedFingerprint = clearFleetCompletionNoticeForSession(
      app.fleetCompletionNotifiedFingerprint,
      host.sessionKey,
    );
    setLastActiveSessionKey(
      host as unknown as Parameters<typeof setLastActiveSessionKey>[0],
      host.sessionKey,
    );
  }
  if (ok && opts?.restoreDraft && opts.previousDraft?.trim()) {
    host.chatMessage = opts.previousDraft;
  }
  if (ok && opts?.restoreAttachments && opts.previousAttachments?.length) {
    host.chatAttachments = opts.previousAttachments;
  }
  scheduleChatScroll(host as unknown as Parameters<typeof scheduleChatScroll>[0]);
  if (ok && !host.chatRunId) {
    void flushChatQueue(host);
  }
  if (ok && opts?.refreshSessions && runId) {
    host.refreshSessionsAfterChat.add(runId);
  }
  return ok;
}

async function flushChatQueue(host: ChatHost) {
  if (!host.connected || isChatBusy(host)) {
    return;
  }
  const [next, ...rest] = host.chatQueue;
  if (!next) {
    return;
  }
  host.chatQueue = rest;
  const ok = await sendChatMessageNow(host, next.text, {
    attachments: next.attachments,
    refreshSessions: next.refreshSessions,
  });
  if (!ok) {
    host.chatQueue = [next, ...host.chatQueue];
  }
}

/**
 * Pillar G — Truncate-and-retry handler for `max_length` /
 * `attachment_max_length` guardrail blocks.
 *
 * The chat-guardrail-banner emits this callback when the user clicks
 * "Send first {limit} chars" / "Send last {limit} chars". We swap the
 * composer draft for the trimmed text, clear the error banner, and
 * re-issue the send. We do NOT keep the original draft as the
 * composer's pending text — the trimmed variant IS the user's chosen
 * new draft.
 */
export async function handleChatGuardrailTruncateRetry(
  host: ChatHost,
  trimmed: string,
): Promise<void> {
  host.chatMessage = trimmed;
  syncChatComposeDraft(trimmed);
  const app = host as unknown as OpenClawApp;
  if (typeof app.lastError === "string" && /chat guardrail/i.test(app.lastError)) {
    app.lastError = null;
  }
  await sendChatMessageNow(host, trimmed, {
    refreshSessions: true,
  });
}

export function removeQueuedMessage(host: ChatHost, id: string) {
  host.chatQueue = host.chatQueue.filter((item) => item.id !== id);
}

export function clearChatPendingMessageEdit(host: ChatHost) {
  const app = host as unknown as OpenClawApp;
  app.chatPendingMessageEdit = null;
  app.chatEditRewindConfirm = null;
}

export function stageChatMessageEdit(
  host: ChatHost,
  rewindTarget: MessageRewindTarget,
  draft: string,
) {
  const app = host as unknown as OpenClawApp;
  const sessionKey = host.sessionKey.trim();
  if (!sessionKey) {
    return;
  }
  app.chatPendingMessageEdit = { sessionKey, rewindTarget };
  app.chatEditRewindConfirm = null;
  host.chatMessage = draft;
  syncChatComposeDraft(draft);
  scheduleChatScroll(host as unknown as Parameters<typeof scheduleChatScroll>[0]);
  requestAnimationFrame(() => {
    document.querySelector<HTMLTextAreaElement>(".chat-compose--fleet textarea")?.focus();
  });
}

export async function confirmChatEditRewindAndSend(host: ChatHost): Promise<boolean> {
  const app = host as unknown as OpenClawApp;
  const payload = app.chatEditRewindConfirm;
  if (!payload) {
    return false;
  }
  const ok = await rewindChatBeforeMessage(app, rewindTargetToRewindParams(payload.rewindTarget));
  app.chatEditRewindConfirm = null;
  app.chatPendingMessageEdit = null;
  if (!ok) {
    host.chatMessage = payload.message;
    host.chatAttachments = payload.attachments.map((att) => ({ ...att }));
    syncChatComposeDraft(payload.message);
    return false;
  }
  await sendChatMessageNow(host, payload.message, {
    attachments: payload.attachments.length > 0 ? payload.attachments : undefined,
    refreshSessions: true,
  });
  return true;
}

export function cancelChatEditRewindConfirm(host: ChatHost) {
  const app = host as unknown as OpenClawApp;
  const payload = app.chatEditRewindConfirm;
  app.chatEditRewindConfirm = null;
  if (payload) {
    host.chatMessage = payload.message;
    host.chatAttachments = payload.attachments.map((att) => ({ ...att }));
    syncChatComposeDraft(payload.message);
  }
}

export async function handleEditUserMessage(
  host: ChatHost,
  rewindTarget: MessageRewindTarget,
  draft: string,
) {
  stageChatMessageEdit(host, rewindTarget, draft);
}

export function dismissPlanImplementOffer(host: ChatHost, fingerprint: string) {
  const app = host as unknown as OpenClawApp;
  app.planImplementDismissedFingerprint = dismissPlanImplementFingerprint(
    app.planImplementDismissedFingerprint ?? {},
    host.sessionKey,
    fingerprint,
  );
}

export async function handleImplementPlan(host: ChatHost) {
  const app = host as unknown as OpenClawApp;
  const runActive = isChatBusy(host) || host.chatStream != null;
  const offer = evaluatePlanImplementOffer({
    sessionKey: host.sessionKey,
    composerMode: host.chatComposerMode ?? app.chatComposerMode ?? "plan",
    messages: host.chatMessages ?? app.chatMessages ?? [],
    runActive,
    dismissedFingerprint: app.planImplementDismissedFingerprint[host.sessionKey.trim()] ?? null,
  });
  if (offer.fingerprint) {
    app.planImplementDismissedFingerprint = dismissPlanImplementFingerprint(
      app.planImplementDismissedFingerprint,
      host.sessionKey,
      offer.fingerprint,
    );
  }
  app.handleChatComposerModeChange("agent");
  await handleSendChat(host, PLAN_IMPLEMENT_PROMPT);
}

export async function handleRegenerateAssistant(host: ChatHost) {
  await regenerateChatReply(host as unknown as OpenClawApp);
}

export function editQueuedMessage(host: ChatHost, id: string) {
  const item = host.chatQueue.find((entry) => entry.id === id);
  if (!item) {
    return;
  }
  host.chatMessage = item.text;
  host.chatAttachments = item.attachments ? item.attachments.map((att) => ({ ...att })) : [];
  host.chatQueue = host.chatQueue.filter((entry) => entry.id !== id);
  syncChatComposeDraft(host.chatMessage);
  scheduleChatScroll(host as unknown as Parameters<typeof scheduleChatScroll>[0]);
}

/** Align with DEFAULT_SUBAGENT_MAX_CONCURRENT in src/config/agent-limits.ts */
const FANOUT_CAP = 8;

async function trySpawnDelegation(host: ChatHost, rawMessage: string): Promise<boolean> {
  const app = host as unknown as OpenClawApp;
  if (!host.client || !rawMessage.trim()) {
    return false;
  }
  const agents = host.agentsList?.agents ?? [];
  const manual = parseMessageForSubAgent(rawMessage, agents);
  const auto =
    manual.kind === "none" ? classifyAutoDelegation(rawMessage) : { action: "main" as const };

  if (
    manual.kind === "spawn" &&
    shouldSendToMainSessionInsteadOfSpawn(host.sessionKey, manual.agentId)
  ) {
    return false;
  }

  const shouldSpawn =
    manual.kind === "spawn" ||
    manual.kind === "all" ||
    manual.kind === "active" ||
    manual.kind === "best-of-n" ||
    (manual.kind === "none" && auto.action === "spawn" && auto.task.trim().length > 0);

  if (!shouldSpawn) {
    return false;
  }

  if (isSubagentSessionKey(host.sessionKey)) {
    return false;
  }

  const task =
    manual.kind === "spawn"
      ? manual.task
      : manual.kind === "all" || manual.kind === "active" || manual.kind === "best-of-n"
        ? manual.task
        : auto.action === "spawn"
          ? auto.task
          : rawMessage;

  const agentDisplayName = (agentId: string) => {
    const agent = agents.find((a) => a.id === agentId);
    return agent?.name ?? agentId;
  };
  const label = (agentId: string) => subagentSpawnLabel(agentDisplayName(agentId), agentId);

  let model: string | undefined;
  if (manual.kind === "none" && auto.action === "spawn" && shouldRouteTrivialToOllama(auto)) {
    try {
      const listed = await host.client.request<{
        models?: GatewayModelChoice[];
      }>("models.list", {});
      const raw = Array.isArray(listed?.models) ? listed.models : [];
      const modelChoices: GatewayModelChoice[] = raw.map((m) => ({
        id: m.id,
        provider: m.provider,
        name: m.name?.trim() ? m.name : m.id,
      }));
      model = pickOllamaModel(modelChoices);
    } catch {
      model = undefined;
    }
  }

  const addUserMessage = () => {
    // Tag optimistic user messages with a stable `id` + `pending: true` so
    // `mergeWithPendingUserMessages` can collapse the canonical server
    // version once it lands (drift outside the 60s content fallback no
    // longer creates a duplicate). The id is also used as the dedupe key
    // for any subsequent duplicate insert (e.g. from `injectChatMessage`).
    const now = Date.now();
    const optimisticId = generateUUID();
    // Idempotent append — a second call from a re-entrant delegation
    // path, reconnect-triggered replay, or repeated manual send must
    // never produce two copies of the same user message.
    app.chatMessages = appendOptimisticUserMessage(app.chatMessages ?? [], {
      id: optimisticId,
      role: "user",
      content: [{ type: "text", text: rawMessage }],
      timestamp: now,
      pending: true,
    });
  };

  const currentAgentId = parseAgentSessionKey(host.sessionKey)?.agentId ?? "main";

  if (manual.kind === "all") {
    const allowedIds = agents
      .filter((agent) => agent.id !== currentAgentId)
      .map((agent) => agent.id)
      .slice(0, FANOUT_CAP);
    if (allowedIds.length === 0) return false;
    addUserMessage();
    const orchestrationId = generateUUID();
    const cards = allowedIds.map((agentId) =>
      createPendingSubagentCard(task, agentId, label(agentId)),
    );
    cards.forEach((c) => {
      c.orchestrationId = orchestrationId;
    });
    app.chatSubagents = [...(app.chatSubagents ?? []), ...cards];
    await Promise.allSettled(
      cards.map((card) =>
        requestSessionsSpawn(host.client!, {
          sessionKey: host.sessionKey,
          task,
          label: card.label,
          agentId: card.agentId,
          model,
          spawnSource: "planner",
        }).then((result) => {
          app.chatSubagents = mergeSpawnResult(host, app.chatSubagents ?? [], card.id, result);
        }),
      ),
    );
    scheduleChatScroll(app as unknown as Parameters<typeof scheduleChatScroll>[0]);
    // JAISIU-3320 / Pillar I — pin the session as the active fan-out
    // target when ≥3 children were dispatched so the "Return to active
    // job" banner can pick it up. The banner is gated on the pinned
    // session being different from the active tab, so navigating away
    // is the natural moment to surface it.
    if (cards.length >= 3) {
      const appForPin = app as unknown as {
        setPinnedActiveJob?: (key: string | null) => void;
      };
      appForPin.setPinnedActiveJob?.(host.sessionKey);
    }
    return true;
  }

  if (manual.kind === "active") {
    const activeIds = getActiveAgentIds(
      (app.chatSubagents ?? []).map((c) => ({ agentId: c.agentId, status: c.status })),
    );
    if (activeIds.length === 0) return false;
    addUserMessage();
    const orchestrationId = generateUUID();
    const cards = activeIds.map((agentId) =>
      createPendingSubagentCard(task, agentId, label(agentId)),
    );
    cards.forEach((c) => {
      c.orchestrationId = orchestrationId;
    });
    app.chatSubagents = [...(app.chatSubagents ?? []), ...cards];
    await Promise.allSettled(
      cards.map((card) =>
        requestSessionsSpawn(host.client!, {
          sessionKey: host.sessionKey,
          task,
          label: card.label,
          agentId: card.agentId,
          model,
          spawnSource: "planner",
        }).then((result) => {
          app.chatSubagents = mergeSpawnResult(host, app.chatSubagents ?? [], card.id, result);
        }),
      ),
    );
    scheduleChatScroll(app as unknown as Parameters<typeof scheduleChatScroll>[0]);
    // JAISIU-3320 / Pillar I — pin the session as the active fan-out
    // target when ≥3 children were dispatched so the "Return to active
    // job" banner can pick it up. The banner is gated on the pinned
    // session being different from the active tab, so navigating away
    // is the natural moment to surface it.
    if (cards.length >= 3) {
      const appForPin = app as unknown as {
        setPinnedActiveJob?: (key: string | null) => void;
      };
      appForPin.setPinnedActiveJob?.(host.sessionKey);
    }
    return true;
  }

  if (manual.kind === "best-of-n") {
    const count = Math.min(manual.n, FANOUT_CAP);
    const pool = agents.slice(0, count);
    if (pool.length === 0) return false;
    addUserMessage();
    const orchestrationId = generateUUID();
    const cards = pool.map((agent) => createPendingSubagentCard(task, agent.id, label(agent.id)));
    cards.forEach((c) => {
      c.orchestrationId = orchestrationId;
    });
    app.chatSubagents = [...(app.chatSubagents ?? []), ...cards];
    await Promise.allSettled(
      cards.map((card) =>
        requestSessionsSpawn(host.client!, {
          sessionKey: host.sessionKey,
          task,
          label: card.label,
          agentId: card.agentId,
          model,
          spawnSource: "planner",
        }).then((result) => {
          app.chatSubagents = mergeSpawnResult(host, app.chatSubagents ?? [], card.id, result);
        }),
      ),
    );
    scheduleChatScroll(app as unknown as Parameters<typeof scheduleChatScroll>[0]);
    // JAISIU-3320 / Pillar I — pin the session as the active fan-out
    // target when ≥3 children were dispatched so the "Return to active
    // job" banner can pick it up. The banner is gated on the pinned
    // session being different from the active tab, so navigating away
    // is the natural moment to surface it.
    if (cards.length >= 3) {
      const appForPin = app as unknown as {
        setPinnedActiveJob?: (key: string | null) => void;
      };
      appForPin.setPinnedActiveJob?.(host.sessionKey);
    }
    return true;
  }

  if (manual.kind === "none" && auto.action === "spawn") {
    const chosen = pickBestFitAgentIds({ agents, task, max: Math.min(FANOUT_CAP, 3) });
    if (chosen.length > 1) {
      addUserMessage();
      const orchestrationId = generateUUID();
      const cards = chosen.map((id) => createPendingSubagentCard(task, id, `auto · ${label(id)}`));
      cards.forEach((c) => {
        c.orchestrationId = orchestrationId;
      });
      app.chatSubagents = [...(app.chatSubagents ?? []), ...cards];
      await Promise.allSettled(
        cards.map((card) =>
          requestSessionsSpawn(host.client!, {
            sessionKey: host.sessionKey,
            task,
            label: card.label,
            agentId: card.agentId,
            model,
            spawnSource: "planner",
          }).then((result) => {
            app.chatSubagents = mergeSpawnResult(host, app.chatSubagents ?? [], card.id, result);
          }),
        ),
      );
      scheduleChatScroll(app as unknown as Parameters<typeof scheduleChatScroll>[0]);
      // JAISIU-3320 / Pillar I — pin the session as the active fan-out
      // target when ≥3 children were dispatched so the "Return to active
      // job" banner can pick it up. The banner is gated on the pinned
      // session being different from the active tab, so navigating away
      // is the natural moment to surface it.
      if (cards.length >= 3) {
        const appForPin = app as unknown as {
          setPinnedActiveJob?: (key: string | null) => void;
        };
        appForPin.setPinnedActiveJob?.(host.sessionKey);
      }
      return true;
    }
  }

  const agentId =
    manual.kind === "spawn"
      ? manual.agentId
      : (pickBestFitAgentIds({ agents, task, max: 1 })[0] ??
        parseAgentSessionKey(host.sessionKey)?.agentId ??
        host.agentsList?.defaultId ??
        "main");
  const agentLabel =
    manual.kind === "spawn" ? (manual.agentName ?? manual.agentId) : `auto · ${label(agentId)}`;

  addUserMessage();
  const card = createPendingSubagentCard(task, agentId, agentLabel);
  app.chatSubagents = [...(app.chatSubagents ?? []), card];

  const result = await requestSessionsSpawn(host.client, {
    sessionKey: host.sessionKey,
    task,
    label: card.label,
    agentId: manual.kind === "spawn" ? manual.agentId : undefined,
    model,
    spawnSource: "planner",
  });

  app.chatSubagents = mergeSpawnResult(host, app.chatSubagents ?? [], card.id, result);
  scheduleChatScroll(app as unknown as Parameters<typeof scheduleChatScroll>[0]);
  return true;
}

export async function handleSendChat(
  host: ChatHost,
  messageOverride?: string,
  opts?: { restoreDraft?: boolean },
) {
  if (!host.connected) {
    return;
  }
  if (messageOverride == null) {
    flushComposeDraftFromDom(host);
  }
  if (host.chatSending && !host.chatRunId && host.chatStreamStartedAt == null) {
    host.chatSending = false;
  }
  const previousDraft = host.chatMessage;
  const message = (messageOverride ?? host.chatMessage).trim();
  const attachments = host.chatAttachments ?? [];
  const attachmentsToSend = messageOverride == null ? attachments : [];
  const hasAttachments = attachmentsToSend.length > 0;

  // Allow sending with just attachments (no message text required)
  if (!message && !hasAttachments) {
    return;
  }

  if (isChatStopCommand(message)) {
    // JAISIU-3503 — clear the textarea + mirror the empty draft into
    // the persistence layer so the user does not see "/stop" lingering
    // in the box after the abort fires. Without this, the operator
    // reads the composer as "the stop did nothing" and concludes the
    // stop activity is broken. We do not surface an optimistic user
    // message: a stop is a control-plane action, not a turn.
    host.chatMessage = "";
    host.chatAttachments = [];
    syncChatComposeDraft("");
    await handleAbortChat(host);
    return;
  }

  if (isChatResetCommand(message)) {
    host.chatMessage = "";
    host.chatAttachments = [];
    syncChatComposeDraft("");
    const app = host as unknown as OpenClawApp;
    app.wireChatControllerState(app);
    const resetResult = await resetChatSession(app);
    if (resetResult.ok) {
      applyResetSessionIdentityHost(app, resetResult);
      app.resetChatScroll();
      void loadSessions(app);
      void app.refreshChatHistorySessions?.();
    } else {
      app.lastError = app.lastError ?? "Failed to reset chat session.";
    }
    return;
  }

  const refreshSessions = true;

  if (
    host.chatRunId &&
    !host.chatSending &&
    ((!host.chatStreamStartedAt && !host.chatStream && !host.chatReasoningStream) ||
      (typeof host.chatStreamStartedAt === "number" &&
        Date.now() - host.chatStreamStartedAt > 45_000))
  ) {
    // JAISIU-XXXX — the 45s stale-run guard. Without this cleanup the
    // operator sees a stranded reasoning-only bubble (chatStream null,
    // chatReasoningStream non-null) long after the run is dead. The
    // guard now resets ALL three stream fields in lockstep so the
    // pending-bubble render path collapses cleanly.
    host.chatStream = null;
    host.chatReasoningStream = null;
    host.chatStreamStartedAt = null;
    host.chatRunId = null;
    // The 45s stale-run guard only clears the run id; without
    // clearChatWaitState the in-flight setInterval keeps firing and the
    // bubble keeps measuring elapsed time, even though no run is active.
    clearChatWaitState(host as unknown as Parameters<typeof clearChatWaitState>[0]);
  }

  if (isChatBusy(host)) {
    const appBusy = host as unknown as OpenClawApp;
    const queueMode = appBusy.settings.fleetDefaultQueueMode ?? "steer";
    const wantsSteer = queueMode === "steer" || queueMode === "steer-backlog";
    if (wantsSteer && message && !hasAttachments) {
      if (messageOverride == null) {
        host.chatMessage = "";
        host.chatAttachments = [];
        syncChatComposeDraft("");
      }
      const steered = await steerChatMessage(appBusy, message);
      if (steered === "steered") {
        scheduleChatScroll(appBusy as unknown as Parameters<typeof scheduleChatScroll>[0]);
        return;
      }
      if (steered === "not-streaming") {
        // The run finished before the steer landed. chatRunId/stream flags were
        // already cleared, so fall through to a fresh chat.send instead of
        // queueing behind a phantom busy state.
        if (messageOverride == null) {
          host.chatMessage = "";
          host.chatAttachments = [];
          syncChatComposeDraft("");
        }
      } else {
        if (messageOverride == null) {
          host.chatMessage = "";
          host.chatAttachments = [];
          syncChatComposeDraft("");
        }
        enqueueChatMessage(host, message, attachmentsToSend, refreshSessions);
        return;
      }
    } else {
      if (messageOverride == null) {
        host.chatMessage = "";
        host.chatAttachments = [];
        syncChatComposeDraft("");
      }
      enqueueChatMessage(host, message, attachmentsToSend, refreshSessions);
      return;
    }
  }

  const app = host as unknown as OpenClawApp;
  const pendingEdit = app.chatPendingMessageEdit;
  if (
    messageOverride == null &&
    pendingEditMatchesSession(pendingEdit, host.sessionKey) &&
    !app.chatEditRewindConfirm
  ) {
    app.chatEditRewindConfirm = {
      sessionKey: host.sessionKey.trim(),
      rewindTarget: pendingEdit.rewindTarget,
      message,
      attachments: attachmentsToSend.flatMap((att) =>
        att.dataUrl ? [{ id: att.id, dataUrl: att.dataUrl, mimeType: att.mimeType }] : [],
      ),
    };
    host.chatMessage = "";
    host.chatAttachments = [];
    syncChatComposeDraft("");
    return;
  }
  const effectiveComposerMode = resolveEffectiveChatComposerMode({
    sessionKey: host.sessionKey,
    globalMode: app.settings.chatComposerMode,
    bySession: app.settings.chatComposerModeBySession,
    tabComposerMode: app.chatTabMeta[host.sessionKey]?.composerMode,
  });
  host.chatComposerMode = effectiveComposerMode;
  if (effectiveComposerMode === "plan" && messageOverride?.trim() !== PLAN_IMPLEMENT_PROMPT) {
    app.planImplementDismissedFingerprint = clearPlanImplementDismissForSession(
      app.planImplementDismissedFingerprint,
      host.sessionKey,
    );
  }
  const modeSendOpts = resolveComposerModeSendOptions(effectiveComposerMode);
  let orchestrate = app.chatSendOrchestrateAll;
  let createNewProject = app.chatSendCreateNewProject;
  let selfImprovement = app.chatSendSelfImprovement;
  if (modeSendOpts.orchestrateWithAll !== undefined) {
    orchestrate = modeSendOpts.orchestrateWithAll;
  }
  if (modeSendOpts.createNewProject !== undefined) {
    createNewProject = modeSendOpts.createNewProject;
  }
  if (modeSendOpts.selfImprovement !== undefined) {
    selfImprovement = modeSendOpts.selfImprovement;
  }
  if (!hasAttachments && app.chatSendSelfImprovement && !selfImprovement) {
    app.lastError = "Switch composer to Agent to use Self improvement.";
    if (messageOverride == null) {
      host.chatMessage = previousDraft;
      host.chatAttachments = attachments;
    }
    return;
  }
  let sendProjectId = app.chatSendProjectId;

  // A message that *looks* like a team job is not refused.
  //
  // This used to block the send with "Multi-agent tasks on the main session
  // only get HEARTBEAT_OK" whenever the draft merely *mentioned* multi-agent
  // work. Observed 2026-10-20: a pasted write-up about the multi-agent
  // orchestration could not be sent, and the operator could not even paste
  // the error back to report it — the error text itself contains
  // "Multi-agent", so it tripped the same guard.
  //
  // The advisory hint above the composer already says what the operator
  // needs to know ("Sounds like a team job. Your message will stay on this
  // session unless you delegate"), and it is shown *while they type* — the
  // right moment to act on it. Refusing the message at send time is a hard
  // wall for a guess, and a long prompt is not automatically a team job.
  if (messageOverride == null) {
    host.chatMessage = "";
    host.chatAttachments = [];
    syncChatComposeDraft("");
  }

  app.lastError = null;

  if (!hasAttachments && createNewProject && app.connected) {
    const projectName = deriveProjectNameFromMessage(message);
    await app.handleCreateFleetProject(projectName);
    if (app.lastError) {
      if (messageOverride == null) {
        host.chatMessage = previousDraft;
        host.chatAttachments = attachments;
      }
      return;
    }
    // Pillar F — route the reset through the per-session mirror so the
    // "create new project" toggle doesn't carry forward after the
    // project has been spawned for this session.
    app.writeChatSendOption("createNewProject", false);
    app.chatSendProjectId = app.chatActiveProjectId;
    app.applySettings({
      ...app.settings,
      fleetChatMainTab: selfImprovement ? "chat" : "fleetMap",
    });
    void app.refreshFleetProjects();
  } else if (!hasAttachments && sendProjectId && app.connected) {
    const existing = findChatContextProject(app.fleetProjects, host.sessionKey, sendProjectId);
    if (!existing) {
      app.lastError = "Selected project is no longer available for this chat.";
      app.syncChatSendProjectSelection();
      return;
    }
    app.chatActiveProjectId = existing.id;
    app.openChatTab(existing.sessionKey);
    if (host.sessionKey.trim() !== existing.sessionKey.trim()) {
      app.activateChatSession(existing.sessionKey);
    }
  }

  if (
    !hasAttachments &&
    orchestrate &&
    app.connected &&
    (!app.agentsList || app.agentsList.agents.length === 0)
  ) {
    await loadAgents(app, { quiet: true });
  }

  if (!hasAttachments && selfImprovement && host.client) {
    const agentId = resolveAgentIdForSession(host) ?? "main";
    const projectResolution = resolveSelfImprovementProjectId({
      projects: app.fleetProjects,
      sessionKey: host.sessionKey,
      chatSendProjectId: app.chatSendProjectId,
      chatActiveProjectId: app.chatActiveProjectId,
    });
    if (!projectResolution.ok) {
      app.lastError = projectResolution.error;
      app.syncChatSendProjectSelection();
      if (messageOverride == null) {
        host.chatMessage = previousDraft;
        host.chatAttachments = attachments;
      }
      return;
    }
    try {
      const res = await startFleetSelfImprovement(host.client, {
        agentId,
        sessionKey: host.sessionKey,
        goal: message,
        projectId: projectResolution.projectId,
      });
      await applyFleetSelfImprovementStarted(
        app,
        host as unknown as Parameters<typeof applyFleetSelfImprovementStarted>[1],
        { goal: message, res },
      );
      return;
    } catch (err) {
      app.lastError = formatFleetSelfImprovementError(err);
      if (messageOverride == null) {
        host.chatMessage = previousDraft;
        host.chatAttachments = attachments;
      }
      return;
    }
  }

  let outbound = message;
  if (!hasAttachments) {
    outbound = applyFleetSendOptionsToMessage(message, {
      orchestrateWithAll: orchestrate,
      createNewProject: createNewProject,
      selfImprovement,
    });
  }

  if (!hasAttachments) {
    const delegated = await trySpawnDelegation(host, outbound);
    if (delegated) {
      app.fleetSendAck = formatFleetDelegationAck(outbound, host.agentsList?.agents ?? []);
      void app.refreshFleetProjects();
      app.applySettings({
        ...app.settings,
        fleetChatMainTab: "fleetMap",
      });
      return;
    }
    if (orchestrate) {
      const agentCount = app.agentsList?.agents?.length ?? 0;
      app.lastError =
        agentCount < 2
          ? "Orchestrate needs at least two agents in Agents."
          : "Orchestrate could not spawn workers. Check gateway logs.";
      return;
    }
  }

  await sendChatMessageNow(host, outbound, {
    previousDraft: messageOverride == null ? previousDraft : undefined,
    restoreDraft: Boolean(messageOverride && opts?.restoreDraft),
    attachments: hasAttachments ? attachmentsToSend : undefined,
    previousAttachments: messageOverride == null ? attachments : undefined,
    restoreAttachments: Boolean(messageOverride && opts?.restoreDraft),
    refreshSessions,
  });
}

export async function refreshChat(host: ChatHost) {
  const app = host as unknown as OpenClawApp;
  const sessionKey = host.sessionKey.trim();
  if (sessionKey) {
    app.chatMessagesBySession.delete(sessionKey);
    const { clearLiveState } = await import("./chat/chat-live-state.ts");
    clearLiveState(app, sessionKey);
  }
  await Promise.all([
    loadChatHistory(app, { force: true, fullHistory: true }),
    // `state.sessionsResult` is the canonical chat-history list — use the
    // dedicated refresh (no `activeMinutes`, no `agentId` filter). The
    // 120-min recency filter was poisoning the chat-history sidebar
    // (clicking an old row would refetch with the filter and the row
    // would vanish from the list).
    app.refreshChatHistorySessions?.(),
    loadAgents(app, { quiet: true }),
    refreshChatAvatar(host),
    app.loadChatModelCatalog(),
  ]);
  const registryRuns =
    app.client && app.connected ? await loadSubagentRegistryRuns(app.client, host.sessionKey) : [];
  app.chatSubagents = mergeRestoredSubagents(
    host.sessionKey,
    app.sessionsResult,
    app.chatSubagents ?? [],
    registryRuns,
  );
  scheduleChatScroll(host as unknown as Parameters<typeof scheduleChatScroll>[0]);
}

export const flushChatQueueForEvent = flushChatQueue;

type SessionDefaultsSnapshot = {
  defaultAgentId?: string;
};

function resolveAgentIdForSession(host: ChatHost): string | null {
  const parsed = parseAgentSessionKey(host.sessionKey);
  if (parsed?.agentId) {
    return parsed.agentId;
  }
  const snapshot = host.hello?.snapshot as
    | { sessionDefaults?: SessionDefaultsSnapshot }
    | undefined;
  const fallback = snapshot?.sessionDefaults?.defaultAgentId?.trim();
  return fallback || "main";
}

function buildAvatarMetaUrl(basePath: string, agentId: string): string {
  const base = normalizeBasePath(basePath);
  const encoded = encodeURIComponent(agentId);
  return base ? `${base}/avatar/${encoded}?meta=1` : `/avatar/${encoded}?meta=1`;
}

export async function refreshChatAvatar(host: ChatHost) {
  if (!host.connected) {
    host.chatAvatarUrl = null;
    return;
  }
  const agentId = resolveAgentIdForSession(host);
  if (!agentId) {
    host.chatAvatarUrl = null;
    return;
  }
  host.chatAvatarUrl = null;
  const url = buildAvatarMetaUrl(host.basePath, agentId);
  try {
    const res = await fetch(url, { method: "GET" });
    if (!res.ok) {
      host.chatAvatarUrl = null;
      return;
    }
    const data = (await res.json()) as { avatarUrl?: unknown };
    const avatarUrl = typeof data.avatarUrl === "string" ? data.avatarUrl.trim() : "";
    host.chatAvatarUrl = avatarUrl || null;
  } catch {
    host.chatAvatarUrl = null;
  }
}

/**
 * Host-shape required by `loadChatHistoryCatalog`. Kept narrow so the
 * function can be unit-tested without spinning up the full `OpenClawApp`
 * Lit element (which has dozens of state fields and a long import graph).
 */
export type ChatHistoryCatalogHost = {
  client: { request: (method: string, params: Record<string, unknown>) => Promise<unknown> } | null;
  connected: boolean;
};

/**
 * Loads the full chat-history catalog for the sidebar.
 *
 * Per operator spec (2026-09-07): the chat-history sidebar is a
 * navigator over saved conversation sessions — the agent context is
 * recovered on click (the session key encodes the agent), so the
 * sidebar does NOT filter by agentId.
 *
 * The earlier JAISIU-3109 fallback logic (`agentIdFallbacks: ["main"]`)
 * tried to cope with the "active session is a subagent" mirror case by
 * re-querying for the `main` agent — but the catalog layer still
 * dropped subagent rows, so for operators whose working sessions all
 * live under `agent:<userId>:subagent:*` the sidebar stayed empty.
 * Asking the gateway without an `agentId` parameter returns every
 * session the operator can see, which is what the spec actually wants.
 *
 * **Pin every default explicitly.** `loadSessions` falls back to
 * persisted state (`state.sessionsFilterActive`,
 * `state.sessionsIncludeGlobal`, `state.sessionsIncludeUnknown`) when an
 * override is `undefined`. An operator who ever touches the Sessions
 * hub filter UI would otherwise silently leak those settings into the
 * chat-history sidebar — the same bug JAISIU-3109 was meant to fix,
 * recreated via a different path. Pinning `activeMinutes: 0`,
 * `includeGlobal: true`, and `includeUnknown: true` here keeps the
 * sidebar's contract independent of the hub's persisted UI state.
 *
 * This is the testable entry point — see
 * `app-chat.loadChatHistoryCatalog.node.test.ts`.
 */
export async function loadChatHistoryCatalog(host: ChatHistoryCatalogHost): Promise<void> {
  if (!host.client || !host.connected) {
    return;
  }
  await loadSessions(
    host as unknown as Parameters<typeof loadSessions>[0],
    {
      limit: 80,
      includeLastMessage: true,
      includeDerivedTitles: true,
      // Pins so the Sessions hub's filter UI cannot poison this list:
      includeGlobal: true,
      includeUnknown: true,
      activeMinutes: 0,
    },
    { quiet: true },
  );
}
